import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, Download, Plus, Search } from 'lucide-react';
import { useCasesList } from '../api/hooks';
import type { InterventionCase } from '../api/types';
import { Badge, Button, Chip, EmptyState, ErrorPanel, Initials, KpiCard, PageHeader, PanelCard, PagedFooter, PillTabs, SortableTh, Spinner, Table, Tabs } from '../components/ui';
import { FaClipboardList } from '../components/ui/icons';
import OpenCaseDialog from '../components/OpenCaseDialog';
import { relativeTime } from '../components/studentRow';
import { caseStatusLabel, caseStatusTone, prettifySlug } from '../lib/caseStatus';
import { activityTitle } from '../lib/caseEvents';
import { dayMonth } from '../lib/dates';
import { downloadCsv, toCsv } from '../lib/csv';
import { useSortableTable, SortColumn } from '../lib/useSortableTable';
import usePagination from '../lib/usePagination';
import { gradeSectionLabel } from './dashboards/sections/format';
import { LINK_BTN } from '../components/ui/linkButton';
import { useAuth } from '../../utils/Auth/AuthProvider';

const DAY = 86_400_000;

const opener = (c: InterventionCase) => c.opened_by_name ?? c.opened_by_username;

/** When the case was last touched, or null when the backend does not send it yet. */
const activityAt = (c: InterventionCase): string | null => (c.last_activity_at !== undefined ? (c.last_activity_at ?? c.opened_at) : null);

const CASES_COLUMNS: SortColumn<InterventionCase>[] = [
  { key: 'id', accessor: (c) => c.id, type: 'number' },
  { key: 'student', accessor: (c) => c.student_name },
  { key: 'category', accessor: (c) => c.category },
  { key: 'status', accessor: (c) => c.status },
  { key: 'opener', accessor: opener },
  { key: 'activity', accessor: (c) => activityAt(c), type: 'date' },
  { key: 'opened', accessor: (c) => c.opened_at, type: 'date' },
  { key: 'outcome', accessor: (c) => c.outcome },
];

type TabKey = 'all' | 'open' | 'in_progress' | 'escalated' | 'resolved';
const TAB_KEYS: TabKey[] = ['all', 'open', 'in_progress', 'escalated', 'resolved'];
const TAB_LABEL: Record<TabKey, string> = {
  all: 'All', open: 'Open', in_progress: 'In progress', escalated: 'Escalated', resolved: 'Resolved',
};

const FIELD_WRAP = 'flex min-h-[40px] items-center gap-2 rounded-[12px] border border-line-strong bg-surface px-3.5 focus-within:outline focus-within:outline-2 focus-within:outline-offset-1 focus-within:outline-accent';
const FIELD_INPUT = 'min-w-0 flex-1 border-0 bg-transparent py-2 text-sm text-ink outline-none placeholder:text-muted';

function median(nums: number[]): number | null {
  if (nums.length === 0) return null;
  const s = [...nums].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

/** CSV of the cases as listed (client side; no endpoint). */
export function casesCsv(cases: InterventionCase[]): string {
  return toCsv(
    ['id', 'student', 'category', 'status', 'opened_by', 'opened_at', 'closed_at', 'outcome'],
    cases.map((c) => [c.id, c.student_name, c.category, c.status, opener(c) ?? '', c.opened_at, c.closed_at ?? '', c.outcome ?? '']),
  );
}

export default function CasesList() {
  const q = useCasesList();
  const navigate = useNavigate();
  const { username } = useAuth();
  const [tab, setTab] = useState<TabKey>('all');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [section, setSection] = useState('');
  const [owner, setOwner] = useState('');
  const [opening, setOpening] = useState(false);

  const all = useMemo(() => q.data ?? [], [q.data]);
  const categories = useMemo(
    () => Array.from(new Set(all.map((c) => c.category).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [all],
  );
  const sections = useMemo(
    () => Array.from(new Set(all.map((c) => c.student_section?.display).filter((d): d is string => !!d))).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    [all],
  );
  const openers = useMemo(
    () => Array.from(new Set(all.map((c) => opener(c)).filter((o): o is string => !!o))).sort((a, b) => a.localeCompare(b)),
    [all],
  );
  const hasActivity = all.some((c) => c.last_activity_at !== undefined);

  // KPIs describe every case; the tab counts follow the student, category and section filters.
  const kpi = useMemo(() => {
    const now = Date.now();
    const count = (s: string) => all.filter((c) => c.status === s).length;
    const resolved = all.filter((c) => c.status === 'resolved');
    const improved = resolved.filter((c) => c.outcome === 'improved').length;
    const days = resolved
      .filter((c) => c.closed_at)
      .map((c) => (new Date(c.closed_at as string).getTime() - new Date(c.opened_at).getTime()) / DAY)
      .filter((d) => d >= 0);
    const idle = (c: InterventionCase) => { const a = activityAt(c); return a == null ? null : (now - new Date(a).getTime()) / DAY; };
    const progressIdle = all.filter((c) => c.status === 'in_progress').map(idle).filter((d): d is number => d != null);
    const escalatedIdle = all.filter((c) => c.status === 'escalated').map(idle);
    return {
      open: count('open'), inProgress: count('in_progress'), escalated: count('escalated'),
      resolved: resolved.length, improved,
      improvedPct: resolved.length > 0 ? Math.round((improved * 100) / resolved.length) : null,
      medianDays: median(days),
      openedThisWeek: all.filter((c) => now - new Date(c.opened_at).getTime() <= 7 * DAY).length,
      // "last touched 1.6 days ago on average" / "1 waiting more than 5 days": only with last-activity data.
      avgIdle: progressIdle.length > 0 ? progressIdle.reduce((a, b) => a + b, 0) / progressIdle.length : null,
      waiting: escalatedIdle.some((d) => d == null) ? null : escalatedIdle.filter((d) => (d as number) > 5).length,
    };
  }, [all]);
  const stillOpen = kpi.open + kpi.inProgress + kpi.escalated;

  const needle = name.trim().toLowerCase();
  const narrowed = useMemo(
    () => all.filter((c) => (!category || c.category === category)
      && (!section || c.student_section?.display === section)
      && (!owner || (owner === '__me' ? !!username && c.opened_by_username === username : opener(c) === owner))
      && (!needle || c.student_name.toLowerCase().includes(needle))),
    [all, category, section, owner, needle, username],
  );
  const shown = tab === 'all' ? narrowed : narrowed.filter((c) => c.status === tab);
  const tabCount = (k: TabKey) => (k === 'all' ? narrowed.length : narrowed.filter((c) => c.status === k).length);

  const { rows, sort, toggle } = useSortableTable(shown, CASES_COLUMNS, { key: 'student', dir: 'asc' });

  const pg = usePagination(rows, 10, `${tab}|${name}|${category}|${section}|${owner}|${sort?.key}|${sort?.dir}`);

  const medianLabel = kpi.medianDays == null ? null : Math.round(kpi.medianDays);
  const subtitle = `${stillOpen} ${stillOpen === 1 ? 'case' : 'cases'} still open, ${kpi.escalated} escalated. ${kpi.resolved} resolved, ${kpi.improved} of them improved.`;

  const exportCsv = () => downloadCsv('cases.csv', casesCsv(rows));

  const clearFilters = () => { setTab('all'); setName(''); setCategory(''); setSection(''); setOwner(''); };

  return (
    <>
      <PageHeader eyebrow="Workflow · intervention cases" title="Cases" subtitle={subtitle}
        actions={<>
          <Link to="/alerts" className={LINK_BTN}><Bell size={18} strokeWidth={1.8} aria-hidden="true" />From alerts</Link>
          <Button variant="ghost" icon={<Download size={18} strokeWidth={1.8} aria-hidden="true" />} disabled={rows.length === 0} onClick={exportCsv}>Export</Button>
          <Button icon={<Plus size={18} strokeWidth={1.8} aria-hidden="true" />} onClick={() => setOpening(true)}>Open a case</Button>
        </>} />

      {q.isLoading ? (
        <PanelCard><div className="flex justify-center py-8"><Spinner size="md" /></div></PanelCard>
      ) : q.isError ? (
        <PanelCard><ErrorPanel body="Could not load cases. Try again." onRetry={() => q.refetch()} /></PanelCard>
      ) : (
        <>
          <div className="mb-[18px] grid grid-cols-1 gap-[18px] min-[821px]:grid-cols-2 min-[1181px]:grid-cols-4">
            <KpiCard label="Open" size="lg" value={kpi.open} chip={<Badge tone="warning">new</Badge>}
              sub={<p>{kpi.openedThisWeek} opened this week</p>} />
            <KpiCard label="In progress" size="lg" value={kpi.inProgress} chip={<Badge tone="info">active</Badge>}
              sub={kpi.avgIdle != null ? <p>last touched {(Math.round(kpi.avgIdle * 10) / 10).toFixed(1)} days ago on average</p> : undefined} />
            <KpiCard label="Escalated" size="lg" tone={kpi.escalated > 0 ? 'danger' : 'default'} value={kpi.escalated} chip={<Badge tone="danger">counselor / admin</Badge>}
              sub={kpi.waiting != null ? <p>{kpi.waiting} waiting more than 5 days</p> : undefined} />
            <KpiCard label="Resolved" size="lg" value={kpi.resolved}
              chip={kpi.improvedPct != null ? <Chip tone="up">{kpi.improvedPct}% improved</Chip> : undefined}
              sub={medianLabel != null ? <p>median {medianLabel} {medianLabel === 1 ? 'day' : 'days'} to resolve</p> : undefined} />
          </div>

          <PanelCard className="overflow-hidden">
            <Tabs accent className="!-mt-1 !mb-4" items={TAB_KEYS.map((k) => ({ key: k, label: TAB_LABEL[k], count: tabCount(k) }))} value={tab} onChange={setTab} label="Case status" />
            <div className="mb-3.5 flex flex-wrap items-center gap-2.5">
              <label className={`${FIELD_WRAP} max-w-[320px] flex-[1_1_220px]`}>
                <Search size={16} strokeWidth={1.8} aria-hidden="true" className="flex-none text-muted" />
                <span className="sr-only">Student</span>
                <input aria-label="Filter by student" type="search" className={FIELD_INPUT} placeholder="Filter by student"
                  value={name} onChange={(e) => setName(e.target.value)} />
              </label>
              {openers.length > 0 && (
                <label className={FIELD_WRAP}>
                  <span className="text-[13px] text-muted">Opened by</span>
                  <select aria-label="Opened by" className={FIELD_INPUT} value={owner} onChange={(e) => setOwner(e.target.value)}>
                    <option value="">Anyone</option>
                    {username && <option value="__me">Me</option>}
                    {openers.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              )}
            </div>
            {categories.length > 0 && (
              <div className="mb-3.5">
                <PillTabs label="Category" dense scroll value={category} onChange={setCategory}
                  options={[{ key: '', label: 'All categories' }, ...categories.map((c) => ({ key: c, label: prettifySlug(c) }))]} />
              </div>
            )}
            {sections.length > 0 && (
              <div className="mb-3.5">
                <PillTabs label="Section" dense scroll value={section} onChange={setSection}
                  options={[{ key: '', label: 'All sections' }, ...sections.map((s) => ({ key: s, label: s }))]} />
              </div>
            )}

            {rows.length === 0 ? (
              all.length > 0 ? (
                <EmptyState icon={<FaClipboardList />} title="No cases match these filters"
                  body={<button type="button" className="font-semibold text-ink underline" onClick={clearFilters}>Clear filters</button>} />
              ) : (
                <EmptyState icon={<FaClipboardList />} title="No cases" body="When you open a case it'll appear here." />
              )
            ) : (
              <Table minWidth={940} className="text-sm">
                <thead>
                  <tr className="text-left [&_th]:border-b [&_th]:border-line">
                    <SortableTh columnKey="id" sort={sort} onToggle={toggle}>#</SortableTh>
                    <SortableTh columnKey="student" sort={sort} onToggle={toggle}>Student</SortableTh>
                    <SortableTh columnKey="category" sort={sort} onToggle={toggle}>Category</SortableTh>
                    <SortableTh columnKey="status" sort={sort} onToggle={toggle}>Status</SortableTh>
                    <SortableTh columnKey="opener" sort={sort} onToggle={toggle}>Opened by</SortableTh>
                    {hasActivity && <SortableTh columnKey="activity" sort={sort} onToggle={toggle}>Last activity</SortableTh>}
                    <SortableTh columnKey="opened" sort={sort} onToggle={toggle}>Opened</SortableTh>
                    <SortableTh columnKey="outcome" sort={sort} onToggle={toggle}>Outcome</SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {pg.pageItems.map((c) => {
                    const who = opener(c);
                    const sectionLine = gradeSectionLabel(c.student_grade, c.student_section);
                    const at = activityAt(c);
                    return (
                      <tr key={c.id} className="cursor-pointer" onClick={() => navigate(`/cases/${c.id}`)}>
                        <td className="border-b border-hairline px-2.5 py-3 align-middle">
                          <Link to={`/cases/${c.id}`} onClick={(e) => e.stopPropagation()} className="font-mono text-[13px] font-semibold text-muted-2 hover:underline">#{c.id}</Link>
                        </td>
                        <td className="border-b border-hairline px-2.5 py-3 align-middle">
                          <Link to={`/cases/${c.id}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-2.5 text-ink">
                            <Initials name={c.student_name} className="!h-7 !w-7 !text-[11px]" />
                            <span>
                              <span className="block font-semibold">{c.student_name}</span>
                              {sectionLine && <span className="text-xs text-muted">{sectionLine}</span>}
                            </span>
                          </Link>
                        </td>
                        <td className="border-b border-hairline px-2.5 py-3 align-middle text-ink">{prettifySlug(c.category)}</td>
                        <td className="border-b border-hairline px-2.5 py-3 align-middle"><Badge tone={caseStatusTone(c.status)}>{caseStatusLabel(c.status)}</Badge></td>
                        <td className="border-b border-hairline px-2.5 py-3 align-middle">
                          {who ? (
                            <span className="inline-flex items-center gap-2 text-[13px] text-ink">
                              <Initials name={who} shape="circle" className="!h-7 !w-7 !text-[11px]" />{who}
                            </span>
                          ) : '—'}
                        </td>
                        {hasActivity && (
                          <td className="border-b border-hairline px-2.5 py-3 align-middle">
                            <span className="block text-[13px] text-ink">{c.last_event ? activityTitle(c.last_event.kind, c.last_event.payload) : 'Case opened'}</span>
                            {at && <span className="text-xs text-muted">{relativeTime(at)}</span>}
                          </td>
                        )}
                        <td className="whitespace-nowrap border-b border-hairline px-2.5 py-3 align-middle font-mono text-[13px] text-muted">{dayMonth(c.opened_at)}</td>
                        <td className="border-b border-hairline px-2.5 py-3 align-middle text-[13px] text-ink">{c.outcome ? prettifySlug(c.outcome) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
            {rows.length > 0 && <PagedFooter from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} className="pt-4" />}
          </PanelCard>
        </>
      )}
      <OpenCaseDialog isOpen={opening} onClose={() => setOpening(false)} />
    </>
  );
}
